import React, { useState } from "react";
import { Card, Divider, Typography, Progress, Tooltip } from "antd";
import ReactApexChart from "react-apexcharts";
import { formatIndian } from "../../../Utils/utils";
import { Link } from "react-router-dom";
import { EyeOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const COLORS = {
  bar: "#1890ff",
  donut: ["#FF6384", "#FF9F40", "#FFCD56", "#4BC0C0", "#36A2EB", "#9966FF"],
  accent: "#5e1a2a",
};

export default function LeadsCard({ data = [], label, data2 = {} }) {
  const [activeIndex, setActiveIndex] = useState(null);

  // Bar chart data
  const barData = [
    { type: "MTD", value: Number(data2.MTD_Value || 0) },
    { type: "YTD", value: Number(data2.YTD_Value || 0) },
  ];

  const cardStyle = {
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    height: "100%",
  };

  const maxBarValue = Math.max(...barData.map((item) => item.value), 1);

  // Donut data
  const pieData = data.map((item) => ({
    name: item.Status || "Unknown",
    value: item.Count || 0,
  }));

  const donutSeries = pieData.map((item) => item.value);
  const donutLabels = pieData.map((item) => item.name);
  const donutTotal = donutSeries.reduce((sum, val) => sum + val, 0);

  const donutChartOptions = {
    chart: {
      type: "donut",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 500,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
      events: {
        dataPointMouseEnter: (event, chartContext, config) => {
          setActiveIndex(config.dataPointIndex);
        },
        dataPointMouseLeave: () => {
          setActiveIndex(null);
        },
      },
    },
    labels: donutLabels,
    colors: COLORS.donut,
    legend: {
      position: "bottom",
      fontSize: "11px",
      formatter: (label, opts) => {
        const value = donutSeries[opts.seriesIndex];
        return `${label} - ${formatIndian(value)}`;
      },
    },
    tooltip: {
      y: {
        formatter: (val) => formatIndian(val),
      },
    },
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "14px",
              fontWeight: 600,
              formatter: function (_, { seriesIndex }) {
                if (seriesIndex !== undefined && seriesIndex !== null) {
                  return donutLabels[seriesIndex];
                }
                return "Total";
              },
            },
            value: {
              show: true,
              fontSize: "16px",
              fontWeight: 600,
              formatter: function (val, { seriesIndex, w }) {
                if (seriesIndex !== undefined && seriesIndex !== null) {
                  return formatIndian(w.globals.series[seriesIndex]);
                }
                return formatIndian(donutTotal);
              },
            },
            total: {
              show: true,
              label: "Total",
              fontSize: "14px",
              fontWeight: 600,
              formatter: function () {
                return formatIndian(donutTotal);
              },
            },
          },
        },
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 260,
          },
          legend: {
            position: "bottom",
            fontSize: "12px",
          },
        },
      },
    ],
  };

  const renderBarChart = () => (
    <div>
      {barData.map((item, index) => (
        <div key={index} style={{ marginBottom: 16 }}>
          <div
            style={{ display: "flex", alignItems: "center", marginBottom: 4 }}
          >
            <Text
              strong
              style={{
                width: 40,
                color: COLORS.bar,
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {item.type}
            </Text>
            <Tooltip
              title={
                <div>
                  <div>
                    <strong>{item.type}</strong>
                  </div>
                  <div>Value: {formatIndian(item.value)}</div>
                  <div>
                    {((item.value / maxBarValue) * 100).toFixed(1)}% of max
                  </div>
                </div>
              }
              placement="top"
            >
              <div style={{ flex: 1, cursor: "pointer", display: "flex", alignItems: "center" }}>
                <Progress
                  percent={(item.value / maxBarValue) * 100}
                  strokeColor={COLORS.bar}
                  trailColor="rgba(163, 117, 119, 0.1)"
                  strokeWidth={7}
                  showInfo={false} // hide default info so we can control it
                  style={{
                    borderRadius: 4,
                    overflow: "hidden",
                    lineHeight: "20px",
                    flex: 1, // let bar take available width
                    marginRight: 8, // space before label
                  }}
                />
                <Text strong style={{ color: COLORS.bar, whiteSpace: "nowrap" }}>
                  {formatIndian(item.value)}
                </Text>
              </div>
            </Tooltip>
          </div>
        </div>
      ))}
    </div>
  );

  const titleStyle = { fontWeight: 600, marginBottom: 12 };

  const pathMap = {
    Lead: "/crm/leads",
    Flexi: "/flexiquote",
    Quotation: "/salesquotation",
    "Sale Order": "/salesorder"
  };

  return (
    <Card style={cardStyle} bodyStyle={{ padding: 12 }}>

      <Title level={5} style={{ ...titleStyle, color: "#c62828" }}>
        Total {label}
      </Title>

      {renderBarChart()}

      <Divider style={{ margin: "16px 0" }} />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Text
          strong
          style={{ color: "#000", display: "block", marginBottom: 16 }}
        >
          {label} Status
        </Text>
        <Link to={pathMap[label] || "#"}>
          <EyeOutlined style={{ fontSize: '16px', cursor: "pointer" }} />
        </Link>
      </div>
      {donutSeries.length > 0 ? (
        <ReactApexChart
          options={donutChartOptions}
          series={donutSeries}
          type="donut"
          height={320}
        />
      ) : (
        <div
          style={{
            height: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Text style={{ color: COLORS.donut[0], fontSize: 16 }}>
            No Data Available
          </Text>
        </div>
      )}
    </Card>
  );
}
